import { createContext, FC, ReactNode, useContext, useEffect, useRef } from "react";
import { useAuth0, User } from "@auth0/auth0-react";
import { setTokenGetter, setLogoutFunction } from "../store/api/base-query";
import { UserRole, UserWithRole } from "../types/user";
import {
  extractUserRole,
  saveUserRoleToStorage,
  clearUserRoleFromStorage,
} from "../utils/role-utils";

const role = "https://quest-iq/roles";

interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  userWithRole: UserWithRole | null;
  isAuthenticated: boolean;
  emailVerified: boolean;
  isAuthenticatedAndVerified: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  setBeforeLogoutHook: (fn: (() => boolean) | null) => void;
  error?: Error;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const {
    user: newUser,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    error,
    getIdTokenClaims,
  } = useAuth0();

  const beforeLogoutHookRef = useRef<(() => boolean) | null>(null);

  const login = () => {
    loginWithRedirect();
  };
  const user = { ...newUser, role: newUser?.[role]?.[0] || "ADMIN" } as User & {
    role?: string[];
  };
  const setBeforeLogoutHook = (fn: (() => boolean) | null) => {
    beforeLogoutHookRef.current = fn;
  };
  const logout = () => {
    if (beforeLogoutHookRef.current && !beforeLogoutHookRef.current()) return;
    clearUserRoleFromStorage();
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Extract and manage user role
  const userRole = extractUserRole(user);

  // Create user with role object
  const userWithRole: UserWithRole | null =
    user && userRole
      ? {
          id: user.sub,
          name: user.name,
          email: user.email,
          email_verified: user.email_verified,
          role: userRole,
        }
      : null;

  // Set up token getter and logout function for RTK Query
  useEffect(() => {
    // Register logout function
    setLogoutFunction(logout);

    if (isAuthenticated && !isLoading) {
      // Create a token getter function that RTK Query can use
      const tokenGetter = async () => {
        try {
          const idTokenClaims = await getIdTokenClaims();
          return idTokenClaims?.__raw || null;
        } catch (error) {
          console.error("Error getting Auth0 token:", error);
          return null;
        }
      };

      // Register the token getter with RTK Query
      setTokenGetter(tokenGetter);
      console.log("🔐 Auth0 token getter registered for RTK Query");
    } else if (!isAuthenticated) {
      // Clear token getter when not authenticated
      setTokenGetter(() => Promise.resolve(null));
      console.log("🔓 Auth0 token getter cleared");
    }
  }, [isAuthenticated, isLoading, getIdTokenClaims, logout]);

  // Handle role persistence to localStorage
  useEffect(() => {
    if (isAuthenticated && userRole && user) {
      // Save role to localStorage when user is authenticated and role is available
      saveUserRoleToStorage(userRole, {
        id: user.sub,
        name: user.name,
        email: user.email,
        email_verified: user.email_verified,
      });
      console.log("🔐 User role saved to localStorage:", userRole);
    } else if (!isAuthenticated) {
      // Clear role from localStorage when not authenticated
      clearUserRoleFromStorage();
    }
  }, [isAuthenticated, userRole, user]);

  // Derive email verification status from user's JWT token
  const emailVerified = user?.email_verified === true;
  const isAuthenticatedAndVerified = isAuthenticated && emailVerified;

  const value: AuthContextType = {
    user: user || null,
    userRole,
    userWithRole,
    isAuthenticated,
    emailVerified,
    isAuthenticatedAndVerified,
    isLoading,
    login,
    logout,
    setBeforeLogoutHook,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
